import React, { useState } from "react";
import { useController } from "react-hook-form";
import { FormMessage } from "./FormMessage";
import { cn } from "helper/Utils";

const InputField = ({
  className,
  inputClassName,
  type = "text",
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  postfix,
  postfixAction,
  control,
  disabled,
  rules,
  pattern, // New prop for regex pattern
  onKeyDown, // New prop for key events
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const {
    field: { value: rhfValue, onChange: rhfOnChange, onBlur: rhfOnBlur, ref },
    fieldState: { error: rhfError },
  } = useController({ name, control, rules }) || {};

  const currentValue = control ? rhfValue : value;
  const currentOnChange = control ? rhfOnChange : onChange;
  const currentOnBlur = control ? rhfOnBlur : onBlur;
  const currentError = control ? rhfError?.message : error;

  // Handle input with regex pattern filtering
  const handleInputChange = (e) => {
    const value = e.target.value;

    // If pattern is provided, validate input in real-time
    if (pattern && value) {
      // For email type, allow typing but don't restrict characters
      if (type === "email") {
        currentOnChange(e);
        return;
      }

      // For other fields with patterns, restrict characters as they type
      const lastChar = value.charAt(value.length - 1);
      if (!pattern.test(value)) {
        // If the whole value doesn't match, revert to previous valid value
        e.target.value = value.slice(0, -1);
      }
    }

    // Call the original onChange handler
    currentOnChange(e);
  };

  return (
    <div className={cn(`input-group relative w-full`, className)}>
      {/* Input Field */}
      <input
        type={type}
        name={name}
        className={cn(
          "bg-white/15 text-white/80 appearance-none rounded text-sm p-4 pb-2.5 pt-6 w-full  border border-transparent outline-none shadow-none",
          "disabled:bg-white/5",
          currentError ? " border-red-600" : "",
          inputClassName ? inputClassName : ""
        )}
        value={currentValue}
        onChange={handleInputChange}
        onBlur={(e) => {
          setIsFocused(false);
          if (currentOnBlur) currentOnBlur(e);
        }}
        onFocus={() => setIsFocused(true)}
        onKeyDown={onKeyDown} // Support for preventing certain keys
        ref={ref}
        disabled={disabled}
      />
      {postfix ? (
        <span onClick={postfixAction} className="absolute inset-y-0 flex items-center h-full right-3 text-sm text-[rgb(255,255,255,.8)]">
          {postfix}
        </span>
      ) : null}
      {/* Label */}
      <label
        htmlFor={name}
        className={`pointer-events-none absolute left-[17px] text-[12px] transition-all duration-200 ease-in-out text-white/50 ${
          isFocused || currentValue ? "top-2.5" : "top-5"
        }`}
      >
        {label}
      </label>

      {/* Error Message */}
      {currentError && <FormMessage message={currentError} />}
    </div>
  );
};

export default InputField;
